import { Node, type JSONContent } from '@tiptap/core';
import { DOMSerializer, type DOMOutputSpec, type ResolvedPos, type Schema } from '@tiptap/pm/model';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { AddMarkStep } from '@tiptap/pm/transform';
import { _js } from '@ifixit/utils/localize';

export const wikiImageAligns = ['left', 'center', 'right'] as const;
export type WikiImageAlign = (typeof wikiImageAligns)[number];

/** Text_Wiki_Parse_Image's sizes, smallest first. */
export const wikiImageSizes = ['standard', 'medium', 'large', 'huge', 'original'] as const;
export type WikiImageSize = (typeof wikiImageSizes)[number];

export const wikiImageSizeLabels: Record<WikiImageSize, string> = {
   standard: _js('Standard'),
   medium: _js('Medium'),
   large: _js('Large'),
   huge: _js('Huge'),
   original: _js('Original'),
};

interface Rendition {
   src: string;
   width?: number;
   height?: number;
}

export interface WikiImageAttrs {
   imageid: string | number;
   size: WikiImageSize;
   align: WikiImageAlign;
   caption: string | null;
   link: string | null;
   newWindow: boolean;
   renditions: Partial<Record<WikiImageSize, Rendition>>;
}

type WikiImageEdit = Partial<Omit<WikiImageAttrs, 'imageid' | 'renditions'>>;

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      wikiImage: {
         /**
          * Changes the selected image. Calls that pass the same undoGroup
          * undo together, so a burst of edits (typing a caption) is one
          * undo step instead of one per keystroke.
          */
         setImageAttributes: (attrs: WikiImageEdit, undoGroup?: symbol) => ReturnType;
      };
   }
}

// Image arguments can't escape anything: a | starts the next argument, an
// argument stops at a line break, and an unmatched ] ends the token.
function argumentText(value: string): string | null {
   const text = value.replace(/[|\r\n]/g, ' ');
   return (hasBalancedBrackets(text) ? text : text.replace(/[[\]]/g, ' ')).trim() || null;
}

function hasBalancedBrackets(text: string): boolean {
   let depth = 0;
   for (const char of text) {
      depth += char === '[' ? 1 : char === ']' ? -1 : 0;
      if (depth < 0) {
         return false;
      }
   }
   return depth === 0;
}

/**
 * The details form's text boxes as attributes that are safe to save. Only
 * what the author changed is touched, so a caption or link that already
 * saves fine stays byte for byte. The page only honors new_window
 * alongside a link.
 */
// Text_Wiki_Parse_Image keeps a link with no scheme as typed (a relative
// Wiki/Battery), so only text that looks like a host ("example.com/x",
// "localhost:3000") gets https://, as the link bubble gives a bare host. A
// scheme ("localhost:" before a port doesn't count) is left for the parser.
const HAS_SCHEME = /^[a-z][\d+.a-z-]*:(?!\d)/i;
const LOOKS_LIKE_HOST = /^[^/?#]*(\.[a-z]|:\d)/i;

export function wikiImageDetails(
   form: { caption: string; link: string; newWindow: boolean },
   current: Pick<WikiImageAttrs, 'caption' | 'link'>
): Pick<WikiImageAttrs, 'caption' | 'link' | 'newWindow'> {
   const caption =
      form.caption === (current.caption ?? '') ? current.caption : argumentText(form.caption);
   let link = form.link === (current.link ?? '') ? current.link : argumentText(form.link);
   if (link && link !== current.link && !HAS_SCHEME.test(link) && LOOKS_LIKE_HOST.test(link)) {
      link = 'https://' + link;
   }
   return { caption, link, newWindow: link !== null && form.newWindow };
}

// Mirrors .imageBox_* in Guide/UI/templates/css/Wiki/wiki.less, so text
// wraps around an image here as it does on the page: side images float
// (except on phones), centered ones stand alone.
const alignClasses: Record<WikiImageAlign, string> = {
   left: 'float-left clear-left mr-[30px] max-[575px]:float-none',
   right: 'float-right clear-right ml-[30px] max-[575px]:float-none',
   center: 'clear-both mx-auto',
};

/**
 * The picture for the image's size. A size with no rendition shows the
 * original, as the page does (Text_Wiki_Render_Xhtml_Image).
 */
function wikiImageRendition(attrs: WikiImageAttrs): Rendition | undefined {
   return attrs.renditions[attrs.size] ?? attrs.renditions.original;
}

/**
 * An image node in its own paragraph, with a picture for each size urlFor
 * has a URL for, or null without the original to fall back on.
 */
export function wikiImageContent(
   imageid: string | number,
   urlFor: (size: WikiImageSize) => unknown
): JSONContent | null {
   const renditions: Partial<Record<WikiImageSize, Rendition>> = {};
   for (const size of wikiImageSizes) {
      const src = urlFor(size);
      if (typeof src === 'string') {
         renditions[size] = { src };
      }
   }
   if (!renditions.original) {
      return null;
   }
   return {
      type: 'paragraph',
      content: [{ type: 'wikiImage', attrs: { imageid, renditions } }],
   };
}

/**
 * An image node goes in as its own paragraph, so only where a paragraph
 * can stand alone. Tables and code blocks are source text and headings
 * plain text, which a paragraph would split. A second paragraph in a list
 * item saves as its own bullet, which the image then leaves empty on the
 * page. All of those get the wikitext instead.
 */
export function canHoldWikiImage(schema: Schema, $pos: ResolvedPos): boolean {
   return (
      Boolean(schema.nodes.wikiImage) &&
      $pos.parent.type.name === 'paragraph' &&
      $pos.node(-1).type.name !== 'listItem'
   );
}

/**
 * An [image|...] token, emitted by the server's Json render
 * (Objects/Text/Wiki/Render/Json/Ifixit/Image.php) and re-emitted as
 * wikitext by to_wiki.ts. It sits inline in its own paragraph, exactly where
 * the server used to put the token's text, so every block that could hold
 * that text can hold the image. renditions is for display and never saved.
 */
export const WikiImage = Node.create({
   name: 'wikiImage',
   group: 'inline',
   inline: true,
   atom: true,
   draggable: true,
   selectable: true,

   addAttributes() {
      return {
         imageid: { default: null },
         size: { default: 'standard' },
         align: { default: 'left' },
         caption: { default: null },
         link: { default: null },
         newWindow: { default: false },
         renditions: { default: {} },
      };
   },

   addCommands() {
      return {
         // Set in place rather than through updateAttributes, which replaces
         // the node and so drops the selection: the image tools would vanish
         // after one click.
         setImageAttributes:
            (attrs, undoGroup) =>
            ({ state, tr, dispatch }) => {
               const { selection } = state;
               if (
                  !(selection instanceof NodeSelection) ||
                  selection.node.type.name !== this.name
               ) {
                  return false;
               }
               if (dispatch) {
                  for (const [name, value] of Object.entries(attrs)) {
                     tr.setNodeAttribute(selection.from, name, value);
                  }
                  // prosemirror-history joins consecutive transactions that
                  // share a composition, the way it keeps IME input together.
                  if (undoGroup) {
                     tr.setMeta('composition', undoGroup);
                  }
               }
               return true;
            },
      };
   },

   // [image|...] always parses as its own block, so bold or a link around
   // one would save as literal markup. The schema can't keep marks off one
   // inline node type, so they're removed as they arrive: from a mark
   // command, or on a pasted or dropped image.
   addProseMirrorPlugins() {
      const { name } = this;
      return [
         // Keeps a floated image at the end of the page inside the editor's
         // border.
         new Plugin({
            key: new PluginKey('wikiImageContainFloats'),
            props: { attributes: { class: 'flow-root' } },
         }),
         new Plugin({
            key: new PluginKey('wikiImageUnmarked'),
            appendTransaction: (transactions, _oldState, state) => {
               const mayMarkImages = transactions.some(
                  tr =>
                     tr.steps.some(step => step instanceof AddMarkStep) ||
                     ['paste', 'drop'].includes(tr.getMeta('uiEvent'))
               );
               if (!mayMarkImages) {
                  return null;
               }
               const tr = state.tr;
               state.doc.descendants((node, pos) => {
                  if (node.type.name === name && node.marks.length > 0) {
                     tr.removeMark(pos, pos + node.nodeSize);
                  }
               });
               return tr.docChanged ? tr : null;
            },
         }),
      ];
   },

   // Copying an image inside the editor round-trips through this HTML, so
   // every attribute rides along as JSON. Images pasted from elsewhere have
   // no imageid to save and are left out.
   parseHTML() {
      return [
         {
            tag: 'span[data-wiki-image]',
            getAttrs: element => {
               try {
                  const attrs = JSON.parse(element.dataset.wikiImage ?? '');
                  return attrs?.imageid ? attrs : false;
               } catch {
                  return false;
               }
            },
         },
      ];
   },

   renderHTML({ node }) {
      return imageSpec(node.attrs as WikiImageAttrs);
   },

   // Updates the image in place rather than rebuilding it from renderHTML,
   // which would blank the picture while the new <img> loads on every
   // alignment, size or caption change.
   addNodeView() {
      return ({ node }) => {
         const dom = renderImage(node.attrs as WikiImageAttrs);
         return {
            dom,
            update: updated => {
               if (updated.type.name !== this.name) {
                  return false;
               }
               syncImage(dom, renderImage(updated.attrs as WikiImageAttrs));
               return true;
            },
         };
      };
   },
});

function imageSpec(attrs: WikiImageAttrs): DOMOutputSpec {
   const rendition = wikiImageRendition(attrs);
   const align = alignClasses[attrs.align] ?? alignClasses.left;
   const image: DOMOutputSpec = [
      'img',
      {
         src: rendition?.src,
         width: rendition?.width,
         height: rendition?.height,
         alt: attrs.caption ?? '',
         draggable: 'false',
         class: 'block h-auto max-w-full',
      },
   ];
   const caption: DOMOutputSpec[] = attrs.caption
      ? [['span', { class: 'block pt-1 text-sm text-gray-600' }, attrs.caption]]
      : [];
   return [
      'span',
      {
         'data-wiki-image': JSON.stringify(attrs),
         class: `${align} flex w-fit max-w-full flex-col [&.ProseMirror-selectednode]:outline-2 [&.ProseMirror-selectednode]:outline-blue-500`,
      },
      image,
      ...caption,
   ];
}

function renderImage(attrs: WikiImageAttrs): HTMLElement {
   return DOMSerializer.renderSpec(document, imageSpec(attrs)).dom as HTMLElement;
}

const SELECTED_CLASS = 'ProseMirror-selectednode';

/**
 * Makes the rendered image look like fresh while keeping its <img>, so an
 * unchanged src never reloads. ProseMirror's selected-node class is kept.
 */
function syncImage(dom: HTMLElement, fresh: HTMLElement) {
   const selected = dom.classList.contains(SELECTED_CLASS);
   copyAttributes(fresh, dom);
   dom.classList.toggle(SELECTED_CLASS, selected);

   const img = dom.querySelector('img');
   const freshImg = fresh.querySelector('img');
   if (img && freshImg) {
      copyAttributes(freshImg, img);
      freshImg.replaceWith(img);
   }
   dom.replaceChildren(...fresh.childNodes);
}

function copyAttributes(from: Element, to: Element) {
   for (const { name } of to.attributes) {
      if (!from.hasAttribute(name)) {
         to.removeAttribute(name);
      }
   }
   for (const { name, value } of from.attributes) {
      if (to.getAttribute(name) !== value) {
         to.setAttribute(name, value);
      }
   }
}
